import { startTransition, useActionState, useState } from "react";
import { useTranslations } from "@/lib/useTranslations";

import { syncPlaceOpeningHours } from "@/api/places";
import { Button } from "@/components/ui/Button";
import type {
  DayOfWeek,
  PlaceOpeningHourEntry,
  PlaceOpeningHours,
  SyncPlaceOpeningHoursResult,
} from "@/core/services/types/place.types";
import { DAYS_OF_WEEK } from "@/core/services/types/place.types";

interface PlaceOpeningHoursPanelProps {
  placeId: string;
  googlePlaceId: string | null;
  initialOpeningHours: PlaceOpeningHours | null;
}

const syncInitialState: SyncPlaceOpeningHoursResult = { success: false, error: "" };

function defaultHours(): PlaceOpeningHourEntry[] {
  return DAYS_OF_WEEK.map((day) => ({ day, isOpen: false, open: "", close: "" }));
}

function normalizeHours(raw: PlaceOpeningHours | null): PlaceOpeningHourEntry[] {
  if (!raw || !Array.isArray(raw) || raw.length === 0) return defaultHours();

  return DAYS_OF_WEEK.map((day) => {
    const entry = raw.find((e) => e.day === day);
    return entry && typeof entry.isOpen === "boolean"
      ? { day, isOpen: entry.isOpen, open: entry.open ?? "", close: entry.close ?? "" }
      : { day, isOpen: false, open: "", close: "" };
  });
}

function Spinner() {
  return (
    <span
      aria-hidden
      className="inline-block size-3.5 animate-spin rounded-full border-2 border-current border-r-transparent"
    />
  );
}

export function PlaceOpeningHoursPanel({
  placeId,
  googlePlaceId,
  initialOpeningHours,
}: PlaceOpeningHoursPanelProps) {
  const t = useTranslations("places.edit");

  const [syncState, syncFormAction, isSyncing] = useActionState(
    syncPlaceOpeningHours,
    syncInitialState,
  );

  const [hours, setHours] = useState<PlaceOpeningHourEntry[]>(() =>
    normalizeHours(initialOpeningHours),
  );

  function updateEntry(day: DayOfWeek, patch: Partial<PlaceOpeningHourEntry>) {
    setHours((prev) =>
      prev.map((entry) => (entry.day === day ? { ...entry, ...patch } : entry)),
    );
  }

  function handleSync() {
    const formData = new FormData();
    formData.set("id", placeId);
    startTransition(() => {
      syncFormAction(formData);
    });
  }

  if (syncState.success && !isSyncing) {
    const synced = normalizeHours(syncState.data.opening_hours_structured);
    if (JSON.stringify(synced) !== JSON.stringify(hours)) {
      setHours(synced);
    }
  }

  return (
    <section className="wireframe-border bg-white p-5 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-whart-text">
          {t("openingHoursLabel")}
        </p>
        <Button
          type="button"
          variant="secondary"
          disabled={isSyncing || !googlePlaceId}
          aria-busy={isSyncing}
          title={!googlePlaceId ? t("openingHoursNoGooglePlaceIdHint") : undefined}
          onClick={handleSync}
          className="inline-flex shrink-0 items-center gap-1.5 text-xs px-3 py-1.5"
        >
          {isSyncing ? <Spinner /> : null}
          {isSyncing ? t("openingHoursSyncing") : t("openingHoursSync")}
        </Button>
      </div>

      {!syncState.success && syncState.error ? (
        <p role="alert" className="text-xs text-whart-urgent">{syncState.error}</p>
      ) : null}

      <div className="space-y-1">
        {hours.map((entry) => (
          <DayRow
            key={entry.day}
            entry={entry}
            disabled={isSyncing}
            label={t(`days.${entry.day}`)}
            onChange={(patch) => updateEntry(entry.day, patch)}
          />
        ))}
      </div>

      {syncState.success ? (
        <p role="status" className="text-xs text-emerald-700">
          {t("openingHoursSyncSuccess")}
        </p>
      ) : null}
    </section>
  );
}

interface DayRowProps {
  entry: PlaceOpeningHourEntry;
  disabled: boolean;
  label: string;
  onChange: (patch: Partial<PlaceOpeningHourEntry>) => void;
}

function DayRow({ entry, disabled, label, onChange }: DayRowProps) {
  const timeInputClass =
    "bg-transparent px-1 py-0.5 text-xs outline-none focus:underline disabled:opacity-30 w-[5rem] [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none";

  return (
    <div className={`flex items-center gap-2 py-0.5 ${!entry.isOpen ? "opacity-40" : ""}`}>
      <span
        role="button"
        tabIndex={0}
        onClick={() => onChange({ isOpen: !entry.isOpen })}
        onKeyDown={(e) => e.key === "Enter" || e.key === " " ? onChange({ isOpen: !entry.isOpen }) : null}
        className="w-20 shrink-0 text-xs font-medium text-neutral-700 cursor-pointer select-none"
      >
        {label}
      </span>
      <input
        type="time"
        value={entry.open}
        disabled={disabled || !entry.isOpen}
        onChange={(e) => onChange({ open: e.target.value })}
        className={timeInputClass}
        aria-label={`${label} opening time`}
      />
      <span className="text-xs text-neutral-400">–</span>
      <input
        type="time"
        value={entry.close}
        disabled={disabled || !entry.isOpen}
        onChange={(e) => onChange({ close: e.target.value })}
        className={timeInputClass}
        aria-label={`${label} closing time`}
      />
    </div>
  );
}
